import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { LockClosedIcon } from "@heroicons/react/outline";
import { useAuthState } from "../../../../Context";
import { isBusinessWriteBlocked } from "../../../../Constants/business";

/**
 * WriteBlockGate — replaces an input form with a quiet "read-only, upgrade to
 * edit" notice when the active business is write-blocked (RESTRICTED, or a
 * free-limit PENDING_DELETION). Matches the relaxed backend write-guard via
 * `isBusinessWriteBlocked`, so it never blocks a still-writable inactivity state.
 *
 * Rendered inside the body of every accounting/cashbook input modal, so a
 * blocked user can't even start typing a record they'd only get a 403 on. The
 * modal already frames the context (its own header/title), so this is a plain
 * centered notice on the modal surface — no nested card — following the house
 * empty-state language (soft accent chip, gray text hierarchy, one brand CTA).
 *
 * Props:
 *  - business: optional override; defaults to the active `user.mainBusiness`.
 *  - children: the form to render when writes are allowed.
 *  - className: extra layout classes for the notice wrapper.
 */
const WriteBlockGate = ({ business, children, className = "" }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuthState();

  const target = business || user?.mainBusiness;

  if (!isBusinessWriteBlocked(target)) return <>{children}</>;

  return (
    <div className={`px-4 py-10 ${className}`}>
      <div className="mx-auto w-full max-w-sm text-center">
        {/* One warm accent — signals "locked / premium", not an error. */}
        <div className="mx-auto mb-5 flex h-11 w-11 items-center justify-center rounded-xl bg-amber-50">
          <LockClosedIcon className="h-5 w-5 text-amber-500" aria-hidden="true" />
        </div>

        <h3 className="text-base font-semibold tracking-tight text-gray-900">
          {t("Editing is locked here")}
        </h3>
        <p className="mx-auto mt-1.5 max-w-xs text-sm leading-relaxed text-gray-500">
          {t(
            "Free accounts can keep only 1 active business. Subscribe to premium to add or edit records for this business.",
          )}
        </p>

        <button
          type="button"
          onClick={() => navigate("/subscription")}
          className="mt-6 inline-flex w-full items-center justify-center rounded-md border border-transparent bg-blue-600 px-4 py-2.5 text-sm font-medium text-white shadow-sm transition-colors duration-200 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          {t("Subscribe to Premium")}
        </button>

        {/* Reassure: the block is input-only — reading still works. */}
        <p className="mt-3 text-xs text-gray-400">
          {t("You can still view and export this business's records")}
        </p>
      </div>
    </div>
  );
};

export default WriteBlockGate;
